import { Arr } from '../../../CodesForFoundation';
import { defineCommand } from '../../../concepts/commands/CommandDefinition';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { booleanStep, numberStep, stringStep } from '../../../concepts/commands/CommandWizard';
import { ObjectManager } from '../../../concepts/objects';
import { resolveSubjects } from '../../canvas/commands/CommandUtil';
import { EditorSession } from '../../canvas/EditorSession';
import { RelativeTo } from '../../canvas/entities/Physical';
import { MyProfileInfo } from '../../canvas/MyProfileInfo';
import { EffectDirector, Effects } from '../../canvas/visualization/EffectDirector';
import { WidgetManager } from '../../canvas/workspace/widgets/WidgetManager';
import { alert } from '../alerts/AlertCommands';
import { SelectedVoteRound } from './SelectedVoteRound';
import { Votable } from './Votable';
import { Vote } from './Vote';
import { VoteIndex } from './VoteIndex';
import { VoteRound } from './VoteRound';
import { VoteSummaryWidget } from './VoteSummaryWidget';
import { getLatestVoteRound, resolveActiveVoteRound, selectLatestVoteRound } from './VotingHelpers';

export const castVote = defineCommand(
    'castVote',
    'Voting: Cast',
    (r, [targets]: [string[]?]) => {

        const dispatch = r.call(CommandDispatcher);
        const effects = r.call(EffectDirector);
        const objects = r.call(ObjectManager);
        const session = r.call(EditorSession);
        const voteIndex = r.call(VoteIndex);

        const subjects = resolveSubjects(r, targets, Votable);
        if (!subjects.length) return;

        const root = objects.getRoot();
        const myProfile = session.get(MyProfileInfo);
        const latestRound = getLatestVoteRound(objects);
        const activeRound = resolveActiveVoteRound(objects, session);

        // Check that the round is active
        if (activeRound !== latestRound && !!activeRound) {
            effects.trigger(Effects.shake, subjects);
            dispatch(alert, ['Once a voting round is complete your votes are frozen and cannot be changed!']);
            return false;
        }

        objects.update(u => {
            // If no active round, start one with default settings
            const round = activeRound ?? objects.get(dispatch(createVoteRound) as string, VoteRound);
            // If no parent it's a new round
            if (!round.parent) {
                round.index = 1;
                root.add(round);
            }

            let hasDuplicateAlerted = false;

            while (subjects.length > 0) {

                // Check that we have enough votes remaining
                if (round.allocation > 0) {
                    const myCount = voteIndex.count(round.id, myProfile.id);
                    if (round.allocation <= myCount) {
                        effects.trigger(Effects.shake, subjects);
                        // eslint-disable-next-line max-len
                        dispatch(alert, [`You have used up all ${round.allocation} of your votes!  You can see what you have voted for by clicking "Spotlight" under your user icon.`]);
                        break;
                    }
                }

                const su = subjects.pop();

                // Check we haven't already voted for this (if we aren't allowed duplicates)
                if (!round.duplicates) {
                    const isDuplicate = Array.from(voteIndex.lookup(round.id, su.id)).find(x => x.userId === myProfile.id);
                    if (isDuplicate) {
                        if (!hasDuplicateAlerted) {
                            dispatch(alert, ['You have already voted for this item and duplicate voting is not allowed this round!']);
                            hasDuplicateAlerted = true;
                        }
                        effects.trigger(Effects.shake, subjects);
                        continue;
                    }
                }

                const vo = u.create(Vote);
                vo.userId = myProfile.id;
                vo.round = round.id;
                su.add(vo);
                effects.send(Effects.text('+1'), su);
                effects.trigger(Effects.text('+1'), su);
            }

        });
    }
);

export const revokeVote = defineCommand(
    'revokeVote',
    'Voting: Revoke',
    (r, [targets]: [string[]?]) => {

        const dispatch = r.call(CommandDispatcher);
        const effects = r.call(EffectDirector);
        const objects = r.call(ObjectManager);
        const session = r.call(EditorSession);
        const voteIndex = r.call(VoteIndex);

        const subjects = resolveSubjects(r, targets, Votable);
        if (!subjects.length) return;

        const myProfile = session.get(MyProfileInfo);
        const latestRound = getLatestVoteRound(objects);
        const activeRound = resolveActiveVoteRound(objects, session);
        if (!activeRound) return;

        // Check that the round is active
        if (activeRound !== latestRound && !!activeRound) {
            effects.trigger(Effects.shake, subjects);
            dispatch(alert, ['Once a voting round is complete your votes are frozen and cannot be changed!']);
            return false;
        }

        objects.update(u => {

            const round = activeRound;
            if (!round) return;

            for (const s of subjects) {
                const v = s.children.find(x => x instanceof Vote && x.round === round.id && x.userId === myProfile.id);
                if (v) {
                    s.remove(v);
                    u.delete(v);
                    effects.trigger(Effects.text('-1'), s);
                }
            }

            // If round is now empty, delete round
            if (voteIndex.count(round.id, '*') === 0) {
                round.removeSelf();
                u.delete(round);
            }

        });
    }
);

export const createVoteRound = defineCommand(
    'createVoteRound',
    'Voting: Create Round',
    (r, [duplicates, allocation]: [boolean?, number?]) => {

        const objects = r.call(ObjectManager);
        const root = objects.getRoot();
        const latestRound = getLatestVoteRound(objects);

        return objects.update(u => {
            const round = u.create(VoteRound);
            round.duplicates = duplicates ?? true;
            round.allocation = allocation ?? 0;
            round.index = (latestRound?.index ?? 0) + 1;
            root.add(round);
            return round.id;
        });

    },
    [
        booleanStep('duplicates?', 'Should duplicate voting be allowed? [Y/n]', true),
        numberStep('allocation', 'How many votes per-person? [0=Unlimited]', 0),
    ]
);

export const deleteVoteRound = defineCommand(
    'deleteVoteRound',
    'Voting: Delete Round',
    (r, [roundId]: [string?]) => {

        const objects = r.call(ObjectManager);
        const session = r.call(EditorSession);
        const voteIndex = r.call(VoteIndex);

        roundId = roundId ?? resolveActiveVoteRound(objects, session)?.id;
        if (!roundId) return;

        const round = objects.get(roundId, VoteRound);
        const roundVotes = voteIndex.lookup(roundId, '*');

        objects.update(u => {
            // Remove and delete all votes
            for (const x of roundVotes) {
                x.removeSelf();
                u.delete(x);
            }
            // Remove and delete round
            round.removeSelf();
            u.delete(round);
        });
    },
    [
        stringStep('roundId'),
    ]
);

export const gotoRound = defineCommand(
    'gotoRound',
    'Voting: Goto Round',
    (r, [dx]: [number]) => {

        const objects = r.call(ObjectManager);
        const session = r.call(EditorSession);

        const allRounds = VoteRound.sort(objects.getAll(VoteRound));
        const latestRound = selectLatestVoteRound(allRounds);
        const activeRound = resolveActiveVoteRound(objects, session);

        const targetIndex = allRounds.indexOf(activeRound) + dx;
        const targetRound = Arr.at(allRounds, targetIndex);

        session.update(SelectedVoteRound, targetRound === latestRound ? null : targetRound.id);
    },
    [
        stringStep('index'),
    ]
);

export const clearVotes = defineCommand(
    'clearVotes',
    'Voting: Clear All',
    (r) => {

        const objects = r.call(ObjectManager);

        const votes = objects.getAll(Vote);
        const rounds = objects.getAll(VoteRound);
        if ((votes.size + rounds.size) === 0) return;

        objects.update(u => {
            for (const x of votes) {
                x.removeSelf();
                u.delete(x);
            }
            for (const x of rounds) {
                x.removeSelf();
                u.delete(x);
            }
        });
    }
);

export const votesOpen = defineCommand(
    'votesOpen',
    null,
    (r, [targetId]: [string?, boolean?]) => {

        const widgets = r.call(WidgetManager);
        widgets.delete('votes');

        const objects = r.call(ObjectManager);
        const subject = objects.getMaybe(targetId, Votable);
        if (!subject) return;

        const badge = subject.layout.tags.votes;
        if (!badge) return;

        const at = badge.offset(subject.position(RelativeTo.Model))
            .pointAt(.5, 0);

        const cont = widgets.createContainer({
            name: 'votes',
            position: ['absolute', { at, relativeTo: 'model' }],
            direction: 'row',
            style: 'none',
        });

        cont.add(widgets.createInline({ ui: VoteSummaryWidget, subject }));
        widgets.add(cont);
        return true;
    },
    [
        stringStep('targetId', 'Target ID (leave blank for selected):'),
    ]
);

export const votesClose = defineCommand(
    'votesOpen',
    null,
    (r) => {
        const widgets = r.call(WidgetManager);
        widgets.delete('votes');
        return true;
    }
);
